import { useState, useEffect, useCallback, useMemo } from "react";
import Table from "components/Common/Table/Table";
import SearchOptions from "components/Common/SearchOptions/SearchOptions";
import * as Database from "utils/database.utils";
import {
    CompanyData,
    WeightsResponse,
    AnalystInfo,
} from "../DummyWeightInputTab/types";
import {
    formatWeight,
    shortenName,
    getAnalystWeight,
    getAnalystRationale,
    buildProcessedData,
    buildTotalsRow,
} from "../utils";
import { BASE_COLUMNS } from "../columns";
import WeightCell from "../WeightCell";
import PortfolioManagerViewTabContainer from "./PortfolioManagerViewTab.styles";

interface Option {
    label: string;
    value: string;
}

interface FilterOption {
    id: number;
    name: string;
    value: string;
    type: string;
    options: Option[];
}

const PortfolioManagerViewTab = () => {
    const [rawData, setRawData] = useState<CompanyData[]>([]);
    const [allAnalysts, setAllAnalysts] = useState<AnalystInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [totals, setTotals] = useState<WeightsResponse["totals"] | null>(
        null
    );
    const [filters, setFilters] = useState<Record<string, string | boolean>>({
        team: false,
    });
    const [teamOptions, setTeamOptions] = useState<Option[]>([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        const teamFilter = filters.team;
        const teamId =
            teamFilter && typeof teamFilter === "string" && teamFilter !== ""
                ? parseInt(teamFilter, 10)
                : null;

        const url =
            teamId !== null
                ? `api/team-dummy-portfolios/all-weights?team_id=${teamId}`
                : `api/team-dummy-portfolios/all-weights`;

        const response = await Database.get(url);
        if (response.ok) {
            const result: WeightsResponse = await response.json();
            setRawData(result.companies);
            setTotals(result.totals);

            const analysts: AnalystInfo[] = result.totals.analyst_totals
                .filter((a) => a.analyst_name)
                .map((a) => ({
                    analyst_id: a.analyst_id,
                    analyst_name: a.analyst_name,
                }));
            setAllAnalysts(analysts);
        } else {
            setRawData([]);
            setAllAnalysts([]);
            setTotals(null);
        }

        setLoading(false);
    }, [filters.team]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fetchTeams = useCallback(async () => {
        try {
            const res = await Database.get("api/company/teams");
            if (res.ok) {
                const data = await res.json();
                const teams: Option[] = [
                    { label: "All Teams", value: "" },
                    ...data.data.map((item: { id: number; name: string }) => ({
                        label: item.name,
                        value: String(item.id),
                    })),
                ];
                setTeamOptions(teams);
            }
        } catch (err) {
            console.error("Error fetching teams:", err);
        }
    }, []);

    useEffect(() => {
        fetchTeams();
    }, [fetchTeams]);

    const filterOptions: FilterOption[] = useMemo(
        () => [
            {
                id: 1,
                name: "Team",
                value: "team",
                type: "DROPDOWN",
                options: teamOptions,
            },
        ],
        [teamOptions]
    );

    const processedData = useMemo(
        () => buildProcessedData(rawData, allAnalysts),
        [rawData, allAnalysts]
    );

    const totalsObj = useMemo(
        () => (totals ? buildTotalsRow(totals, allAnalysts, { team_name: "" }) : null),
        [totals, allAnalysts]
    );

    const TEAM_COLUMN = {
        id: 9,
        title: "TEAM",
        sortable: true,
        value: "team_name",
        cell: (d: string) => d,
        show: true,
    };

    const analystColumns = useMemo(
        () =>
            allAnalysts
                .filter((analyst) => analyst.analyst_name)
                .map((analyst, index) => ({
                    id: 100 + index,
                    title: shortenName(analyst.analyst_name).toUpperCase(),
                    sortable: true,
                    value: `analyst_${analyst.analyst_id}`,
                    cell: (rowOrValue: CompanyData | number) =>
                        typeof rowOrValue === "number" ? (
                            formatWeight(rowOrValue)
                        ) : (
                            <WeightCell
                                weight={getAnalystWeight(
                                    rowOrValue,
                                    analyst.analyst_id
                                )}
                                portfolioWeight={rowOrValue.portfolio_weight}
                                rationale={getAnalystRationale(
                                    rowOrValue,
                                    analyst.analyst_id
                                )}
                                companyId={rowOrValue.company_id}
                                companyName={rowOrValue.company_name}
                                bbgTicker={rowOrValue.bbg_ticker || undefined}
                            />
                        ),
                    show: true,
                    fullDataRow: true,
                })),
        [allAnalysts]
    );

    const columns = useMemo(
        () => [...BASE_COLUMNS, TEAM_COLUMN, ...analystColumns],
        [analystColumns]
    );

    return (
        <PortfolioManagerViewTabContainer>
            <SearchOptions
                setFilterQuery={setFilters}
                options={filterOptions}
                localFilter
                showAll={false}
            />
            <Table
                data={processedData}
                columns={columns}
                filterQuery=""
                emptyText="No data found for selected team"
                defaultSortKey="ptf_delta"
                defaultSortOrder="DESC"
                total={rawData.length}
                loading={loading}
                showTotals={rawData.length > 0}
                totals={totalsObj}
                totalsLabel="TOTAL"
            />
        </PortfolioManagerViewTabContainer>
    );
};

export default PortfolioManagerViewTab;
